import {
  deleteV1CompaniesCompanyIdKmlFilesId,
  getV1CompaniesCompanyIdKmlFiles,
  GetV1CompaniesCompanyIdKmlFilesData,
  patchV1CompaniesCompanyIdKmlFilesId,
  postV1CompaniesCompanyIdKmlFiles,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useState } from 'react';

import { useDirectUploads } from '~hooks/useDirectUploads/useDirectUploads';
import connection from '~services/connectionModule';
import { extractPagination } from '~services/pagination';

import { KMLFile } from './models';

export const useKMLFile = () => {
  const [isUploading, setIsUploading] = useState(false);
  const { createDirectUpload } = useDirectUploads();
  interface FetchKMLFilesParams {
    companyId: string;
    limit?: number;
    cursor?: string;
    signal?: AbortSignal;
  }
  const fetchKMLFiles = async ({
    companyId,
    limit,
    cursor,
    signal,
  }: FetchKMLFilesParams) => {
    const query: Partial<GetV1CompaniesCompanyIdKmlFilesData['query']> = {
      'page[limit]': limit ?? 20,
    };

    if (cursor) {
      query['page[after]'] = cursor;
    }

    try {
      const response = await getV1CompaniesCompanyIdKmlFiles({
        path: { 'company-id': companyId },
        query,
        signal,
      });
      const pagination = extractPagination(response);
      return {
        data: response.data.data.map(KMLFile.parse),
        pagination,
      };
    } catch (error) {
      if (signal?.aborted) {
        throw error;
      }

      console.error(error);
      connection.handleRequestError(error, t('error_messages.kml_file.failed_to_fetch'));
    }
  };

  const createKMLFile = async (companyId: string, file: File) => {
    setIsUploading(true);
    try {
      const directUpload = await createDirectUpload(file);
      const response = await postV1CompaniesCompanyIdKmlFiles({
        path: { 'company-id': companyId },
        body: {
          file: directUpload.signed_id,
        },
      });

      return KMLFile.parse(response.data.data);
    } catch (error) {
      console.error(error);
      connection.handleRequestError(error, t('error_messages.kml_file.failed_to_upload'));
    } finally {
      setIsUploading(false);
    }
  };

  const updateKMLFile = async (companyId: string, id: string, name: string) => {
    try {
      const response = await patchV1CompaniesCompanyIdKmlFilesId({
        path: { 'company-id': companyId, id },
        body: { name },
      });
      return KMLFile.parse(response.data.data);
    } catch (error) {
      console.error(error);
      connection.handleRequestError(error, t('error_messages.kml_file.failed_to_update'));
    }
  };

  const deleteKMLFile = async (companyId: string, kmlFile: KMLFile) => {
    try {
      await deleteV1CompaniesCompanyIdKmlFilesId({
        path: { 'company-id': companyId, id: kmlFile.id },
      });
    } catch (error) {
      console.error(error);
      connection.handleRequestError(error, t('error_messages.kml_file.failed_to_delete'));
    }
  };

  return {
    isUploading,
    fetchKMLFiles,
    createKMLFile,
    updateKMLFile,
    deleteKMLFile,
  };
};
